import { cn } from "@base/lib/utils";
import { AppLink } from "@base/ui/AppLink";
import { useDailySummaryQuery } from "@features/project-detail/model/useDailySummaryQuery";
import { groupWorkItemsByContractor } from "@features/project-detail/model/workHistoryGrouping";
import dayjs from "dayjs";
import { HardHat, Pickaxe, X } from "lucide-react";
import type { ReactNode } from "react";

const TABS = [
  { key: "workforce" as const, label: "Workforce" },
  { key: "equipment" as const, label: "Equipment" },
];

function WorkCountItem({ icon, count, isLoading }: { icon: ReactNode; count: number | undefined; isLoading: boolean }) {
  return (
    <span className="flex items-center gap-2">
      {icon}
      {isLoading ? (
        <div className="h-3.5 w-5 rounded-4 skeleton-shimmer" />
      ) : (
        <span className="text-label2 text-gray-800">{count ?? "-"}</span>
      )}
    </span>
  );
}

function ContentSkeleton() {
  return (
    <div className="animate-pulse bg-gray-10 rounded-8 p-4 flex flex-col gap-6">
      <div className="flex flex-col gap-4">
        {Array.from({ length: 2 }, (_, i) => `skeleton-contractor-${i}`).map((key) => (
          <div key={key} className="flex flex-col gap-1">
            <div className="h-4 w-24 rounded-4 skeleton-shimmer" />
            <div className="flex gap-4 pl-4 pr-2 py-2">
              <div className="w-px self-stretch bg-gray-300 shrink-0" />
              <div className="flex-1 flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <div className="h-3.5 w-32 rounded-4 skeleton-shimmer" />
                  <div className="h-3.5 w-6 rounded-4 skeleton-shimmer" />
                </div>
                <div className="h-px bg-gray-300" />
                <div className="flex items-center justify-between">
                  <div className="h-3.5 w-28 rounded-4 skeleton-shimmer" />
                  <div className="h-3.5 w-6 rounded-4 skeleton-shimmer" />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between pr-1">
        <div className="h-4 w-10 rounded-4 skeleton-shimmer" />
        <div className="h-4 w-8 rounded-4 skeleton-shimmer" />
      </div>
    </div>
  );
}

function ContentError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="bg-gray-10 rounded-8 p-8 flex flex-col items-center gap-3">
      <p className="text-body3 text-gray-500 text-center">Failed to load work history.</p>
      <button type="button" onClick={onRetry} className="text-label2 text-blue-500 underline">
        Retry
      </button>
    </div>
  );
}

export type WorkHistoryTabKey = (typeof TABS)[number]["key"];

interface WorkHistoryDayContentProps {
  projectId: string;
  /** 선택된 날짜 (YYYY-MM-DD). desktop panel/mobile sheet가 같은 값을 공유한다. */
  date: string;
  activeTab: WorkHistoryTabKey;
  onActiveTabChange: (tab: WorkHistoryTabKey) => void;
  /** 지정하면 헤더에 닫기 버튼을 보인다 (mobile bottom sheet). desktop panel은 생략한다. */
  onClose?: () => void;
  /** 선택 날짜의 실제 일지 id (없으면 null). 있으면 하단에 해당 일지로 바로 가는 링크를 보인다. */
  dailyLogId: number | null;
  className?: string;
}

/**
 * 선택한 날짜의 인력/장비 상세. `WorkHistoryBottomSheet`(mobile)와 desktop 우측 panel이
 * 이 컴포넌트 하나를 공유해 같은 query 결과·그룹핑을 그대로 보여준다.
 *
 * 여기서 보여주는 합계는 일별 상세 API의 날짜 총합("Daily total")이다. 좌측 목록의
 * 일지별 수량과는 다른 값일 수 있다 (하루에 일지가 여러 건이면 목록은 일지별, 여기는 날짜 합산).
 */
export function WorkHistoryDayContent({
  projectId,
  date,
  activeTab,
  onActiveTabChange,
  onClose,
  dailyLogId,
  className,
}: WorkHistoryDayContentProps) {
  const { data, isLoading, isError, refetch } = useDailySummaryQuery(projectId, date);

  const activeCategory = data?.[activeTab];
  const groups = activeCategory ? groupWorkItemsByContractor(activeCategory.items) : [];

  return (
    <div className={cn("flex flex-col gap-6", className)}>
      <div className="flex flex-col gap-1">
        <div className="flex justify-between items-center gap-2">
          <h2 className="text-title6 md:text-subtitle4 text-gray-1000">
            {dayjs(`${date}T00:00:00`).format("dddd - MMM DD, YYYY")}
          </h2>
          {onClose && (
            <button type="button" onClick={onClose} aria-label="Close work history">
              <X width={24} height={24} className="text-gray-1000" />
            </button>
          )}
        </div>
        {/* 인력/장비를 한 번에 보여주는 합계 요약은 desktop에서는 아래 segmented tab + Daily total로
            대체되므로(중복) mobile에서만 보인다. */}
        <p className="md:hidden text-label3 text-gray-600">
          Daily total — combined workforce and equipment for this date
        </p>

        <div className="md:hidden flex gap-[18px] items-center mt-1">
          <WorkCountItem
            icon={<HardHat width={16} height={16} />}
            count={data?.workforce.total_quantity}
            isLoading={isLoading}
          />
          <WorkCountItem
            icon={<Pickaxe width={16} height={16} />}
            count={data?.equipment.total_quantity}
            isLoading={isLoading}
          />
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <div className="bg-gray-200 rounded-[6px] p-1 flex gap-1">
          {TABS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              className={cn(
                "flex-1 h-10 flex items-center justify-center rounded-[6px] text-label2 text-gray-1000 transition-all",
                activeTab === key &&
                  "bg-white shadow-[0px_2px_8px_2px_rgba(0,0,0,0.07),0px_1px_2px_0px_rgba(0,0,0,0.09),0px_1px_1px_-1px_rgba(0,0,0,0.1)]",
              )}
              onClick={() => onActiveTabChange(key)}
            >
              {label}
            </button>
          ))}
        </div>

        {isLoading ? (
          <ContentSkeleton />
        ) : isError ? (
          <ContentError onRetry={refetch} />
        ) : (
          <div className="bg-gray-10 rounded-8 p-4 flex flex-col gap-6">
            {groups.length === 0 ? (
              <p className="text-body4 text-gray-500 text-center py-2">No {activeTab} recorded for this date.</p>
            ) : (
              <div className="flex flex-col gap-4">
                {groups.map(({ contractor_id, contractor_name, trades }) => {
                  const subtotal = trades.reduce((sum, trade) => sum + trade.quantity, 0);
                  return (
                    <div key={contractor_id} className="flex flex-col gap-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-subtitle4 text-gray-900">{contractor_name}</span>
                        <span className="hidden md:inline text-label3 text-gray-500 tabular-nums">{subtotal}</span>
                      </div>
                      <div className="flex gap-4 pl-4 pr-2 py-2">
                        <div className="w-0 self-stretch border-l border-gray-300 shrink-0" />
                        <div className="flex-1 flex flex-col min-w-0">
                          {trades.map((trade, index) => (
                            <div key={trade.trade_id}>
                              {index > 0 && <div className="h-px bg-gray-300 my-3" />}
                              <div className="flex items-center justify-between text-label2 text-gray-900">
                                <span>{trade.trade_name}</span>
                                <span>{trade.quantity}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="flex items-center justify-between text-subtitle4 text-gray-1000 pr-1 md:pt-3 md:border-t md:border-gray-200">
              <span>Daily total</span>
              <span>{activeCategory?.total_quantity ?? "-"}</span>
            </div>
          </div>
        )}
      </div>

      {/* 이 날짜에 실제로 작성된 일지가 있을 때만 보인다 — 해당 일지 상세로 바로 이동. */}
      {dailyLogId !== null && (
        <div className="flex items-center justify-between gap-3">
          <span className="text-label3 text-gray-500">Go to this daily log</span>
          <AppLink
            to="/projects/$projectId/log/$logId"
            params={{ projectId, logId: String(dailyLogId) }}
            className="inline-flex shrink-0 items-center h-8 px-3 text-label3 font-medium text-gray-800 whitespace-nowrap bg-white border border-gray-300 rounded-8 hover:bg-gray-50"
          >
            Open daily log
          </AppLink>
        </div>
      )}
    </div>
  );
}
